import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faPaperPlane,
  faSpinner,
  faTimes,
  faRobot,
  faUser,
  faChevronDown,
  faChevronUp,
  faTrash,
  faBolt,
} from '@fortawesome/free-solid-svg-icons';
import { aiChatApi } from '../api/aiChatApi';
import { useAuth } from '../contexts/AuthContext';

const SUGGESTIONS = [
  "What's my buying power?",
  'Show my positions',
  'Is the market open?',
  'Top gainers today',
  'Latest AAPL quote',
  'Recent fills',
];

const AIChat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState([{ name: 'llama3.2' }]);
  const [selectedModel, setSelectedModel] = useState('llama3.2');
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const sessionId = `user-${user?.id || 'guest'}`;

  useEffect(() => {
    loadModels();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const loadModels = async () => {
    try {
      const response = await aiChatApi.listModels();
      if (response.success && response.data) {
        const modelsList = Array.isArray(response.data)
          ? response.data
          : response.data.models || [];
        const list = modelsList
          .map((m) => (typeof m === 'string' ? { name: m } : m))
          .filter((m) => {
            if (!m?.name) return false;
            const caps = m.capabilities;
            if (!Array.isArray(caps) || caps.length === 0) return true;
            return caps.includes('completion');
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        if (list.length) {
          setModels(list);
          setSelectedModel((prev) =>
            list.some((m) => m.name === prev) ? prev : list[0].name
          );
        }
      }
    } catch (err) {
      console.error('Error loading models:', err);
    }
  };

  const handleSend = async (e, preset = null) => {
    if (e?.preventDefault) e.preventDefault();
    const userMessage = (preset || input).trim();
    if (!userMessage || loading) return;

    setInput('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await aiChatApi.chat(userMessage, sessionId, selectedModel);
      if (response.success && response.data) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: response.data.response,
            toolResults: response.data.toolResults,
          },
        ]);
      } else {
        throw new Error(response.error || 'Failed to get response');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to send message';
      setError(msg);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${msg}`, isError: true },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    aiChatApi.clearHistory(sessionId).catch((err) => console.error('Error clearing history:', err));
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => {
            setIsCollapsed(false);
            setIsOpen(true);
          }}
          className="fixed bottom-16 right-4 w-12 h-12 rounded-xl border border-[var(--lt-border-strong)] bg-[var(--lt-surface)] text-[var(--lt-action)] shadow-lg hover:bg-[var(--lt-hover)] transition-colors flex items-center justify-center z-[60]"
          title="AI Trading Assistant"
        >
          <FontAwesomeIcon icon={faComments} className="text-lg" />
        </button>
      )}

      {isOpen && (
        <div
          className={`fixed bottom-16 right-4 w-[26rem] max-w-[calc(100vw-1.5rem)] rounded-xl border border-[var(--lt-border)] bg-[var(--lt-surface)] flex flex-col z-[60] overflow-hidden transition-all duration-200 ${
            isCollapsed ? 'h-14' : 'h-[640px] max-h-[calc(100vh-5rem)]'
          }`}
        >
          <div className="px-3 py-2.5 border-b border-[var(--lt-border)] bg-[var(--lt-input)] flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-md bg-[var(--lt-action-dim)] text-[var(--lt-action)] flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faRobot} className="text-xs" />
              </div>
              <div className="min-w-0">
                <h3 className="text-xs font-semibold text-[var(--lt-text)] truncate">Desk AI</h3>
                <p className="text-[10px] text-[var(--lt-faint)] truncate flex items-center gap-1">
                  <FontAwesomeIcon icon={faBolt} className="text-[8px]" />
                  Ollama · Alpaca MCP tools
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="text-[10px] max-w-[11rem] px-1.5 py-1 rounded bg-[var(--lt-surface)] border border-[var(--lt-border)] text-[var(--lt-muted)] focus:outline-none focus:border-[var(--lt-action)]"
                onClick={(e) => e.stopPropagation()}
                title="Model"
              >
                {models.map((model) => (
                  <option key={model.name} value={model.name}>
                    {model.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setIsCollapsed((prev) => !prev)}
                className="w-7 h-7 rounded text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]"
                title={isCollapsed ? 'Expand' : 'Minimize'}
              >
                <FontAwesomeIcon icon={isCollapsed ? faChevronUp : faChevronDown} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsCollapsed(false);
                  setIsOpen(false);
                }}
                className="w-7 h-7 rounded text-[var(--lt-muted)] hover:bg-[var(--lt-hover)]"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {!isCollapsed && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[var(--lt-bg)]">
                {messages.length === 0 && (
                  <div className="py-4">
                    <p className="text-xs text-[var(--lt-muted)] mb-3">
                      Ask about account, positions, screeners, clock, quotes, activities, or news.
                      Tools mirror{' '}
                      <a
                        href="https://docs.alpaca.markets/us/docs/alpaca-mcp-server"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--lt-action)] hover:underline"
                      >
                        Alpaca MCP
                      </a>
                      .
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {SUGGESTIONS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => handleSend(null, s)}
                          className="px-2 py-1 text-[11px] rounded border border-[var(--lt-border)] text-[var(--lt-muted)] hover:bg-[var(--lt-hover)] hover:text-[var(--lt-text)] text-left"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[88%] rounded-lg px-3 py-2 ${
                        message.role === 'user'
                          ? 'bg-[var(--lt-action-dim)] text-[var(--lt-text)] border border-[rgba(108,140,255,0.35)]'
                          : message.isError
                            ? 'bg-[var(--lt-bear-dim)] text-[var(--lt-bear)] border border-[rgba(240,113,103,0.35)]'
                            : 'bg-[var(--lt-surface)] text-[var(--lt-text)] border border-[var(--lt-border)]'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <FontAwesomeIcon
                          icon={message.role === 'user' ? faUser : faRobot}
                          className="text-[10px] mt-1 text-[var(--lt-faint)] shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs whitespace-pre-wrap leading-relaxed">{message.content}</p>
                          {message.toolResults?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {message.toolResults.map((result, idx) => (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono ${
                                    result.ok === false || result.error
                                      ? 'bg-[var(--lt-bear-dim)] text-[var(--lt-bear)] border-[rgba(240,113,103,0.35)]'
                                      : 'bg-[var(--lt-bull-dim)] text-[var(--lt-bull)] border-[rgba(61,220,151,0.35)]'
                                  }`}
                                  title={result.error || JSON.stringify(result.args || {})}
                                >
                                  {result.tool}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex justify-start">
                    <div className="rounded-lg px-3 py-2 border border-[var(--lt-border)] bg-[var(--lt-surface)] text-[var(--lt-muted)] text-xs">
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2 text-[var(--lt-action)]" />
                      Calling tools…
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="border-t border-[var(--lt-border)] p-2.5 bg-[var(--lt-surface)]">
                {error && (
                  <div className="mb-2 text-[11px] text-[var(--lt-bear)] bg-[var(--lt-bear-dim)] px-2 py-1.5 rounded">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSend} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask the desk…"
                    className="flex-1 px-2.5 py-2 text-xs rounded-md bg-[var(--lt-input)] border border-[var(--lt-border)] text-[var(--lt-text)] placeholder:text-[var(--lt-faint)] focus:outline-none focus:border-[var(--lt-action)]"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={loading || !input.trim()}
                    className="w-9 h-9 rounded-md bg-[var(--lt-action-dim)] text-[var(--lt-action)] border border-[rgba(108,140,255,0.35)] hover:bg-[var(--lt-hover)] disabled:opacity-40"
                  >
                    <FontAwesomeIcon
                      icon={loading ? faSpinner : faPaperPlane}
                      className={loading ? 'animate-spin' : ''}
                    />
                  </button>
                </form>
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="mt-2 text-[10px] text-[var(--lt-faint)] hover:text-[var(--lt-muted)]"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Clear history
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default AIChat;
